// Real GIS map for the demo: OSM base + the live Belgian statistical-sector layer
// (WFS vector, geoserver) with CLICK-TO-SELECT. This replaces the earlier mock-square
// map and reuses the upstream pattern (WFS VectorSource + bbox strategy from
// loadBaseLayers.js, click Select from pcSelection.js) adapted for the standalone demo.
//
// BE has no PC4 polygon layer in base-layers/raw; the finest clickable postcode zone is
// the "PC5"/sector layer geolinq:u101413ViewGroupAdresType1. Each sector feature carries
//   u108264Aantal_adres  -> address/household count
//   u108267Admin_NL      -> NIS-style sector code, e.g. "11002G54-"
// The count endpoint works on 4-digit postcodes, so main.js resolves each clicked
// sector's centroid to a pc4 via /api/geo/postcode-at and counts per pc4.

import { Map, View, Feature } from 'ol';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { bbox as bboxStrategy } from 'ol/loadingstrategy';
import Draw from 'ol/interaction/Draw';
import { fromCircle } from 'ol/geom/Polygon';
import CircleGeom from 'ol/geom/Circle';
import { getDistance } from 'ol/sphere';
import { Style, Fill, Stroke } from 'ol/style';
import { fromLonLat, toLonLat } from 'ol/proj';
import { getCenter } from 'ol/extent';
import MultiPolygon from 'ol/geom/MultiPolygon';
import { GEOSERVER_URL } from '../config.js';
import { state, countKeyOf, sectorPctOf } from './state.js';

// The clickable sector layer (BE). Only load/show when zoomed in enough (bbox strategy
// would otherwise pull the whole country). maxResolution ~30 m/px ≈ zoom >= 12.
const SECTOR_TYPENAME = 'geolinq:u101413ViewGroupAdresType1';
const SECTOR_MAX_RESOLUTION = 40;

const FIELD_ADMIN = ['u108267Admin_NL', 'u108266Admin_FR', 'u108265Admin_GER'];
const FIELD_COUNT = 'u108264Aantal_adres';

let map = null;
let sectorLayer = null;
let sectorSource = null;
let selectionLayer = null;   // highlighted clones of selected sectors
let selectionSource = null;
let clickHandler = null;
let drawLayer = null;
let drawSource = null;
let drawInteraction = null;
let drawSelectCb = null;
let selectEnabled = true; // Select is the default: a click toggles a sector, a drag still pans (no separate Move mode)

const droppedCodes = () => window.__droppedSectors || new Set();

function readAdminCode(feature) {
  for (const f of FIELD_ADMIN) {
    const v = feature.get(f);
    if (v) return String(v).replace(/-+$/, '').trim();
  }
  return null;
}

// Some sectors come from the WFS as two or three separate Polygon rows with the same code
// (42 of the 2.209 codes in West-Vlaanderen, e.g. 43010C081: two pieces of land apart). The app
// keys everything by code, so only the clicked piece got highlighted. Merge the rows into one
// MultiPolygon feature per code; the address count is the sector total on every row (max, not sum).
function polygonsOf(geom) {
  if (!geom) return [];
  if (geom.getType() === 'MultiPolygon') return geom.getPolygons();
  if (geom.getType() === 'Polygon') return [geom];
  return [];
}
function mergeFeaturesByCode(feats) {
  const byCode = new globalThis.Map(); // JS Map: `Map` in this module is the OpenLayers map class
  for (const f of feats) {
    const code = readAdminCode(f);
    if (!code) continue;
    const prev = byCode.get(code);
    if (!prev) { byCode.set(code, f); continue; }
    const polys = [...polygonsOf(prev.getGeometry()), ...polygonsOf(f.getGeometry())];
    if (!polys.length) continue;
    const merged = prev.clone();
    merged.setGeometry(new MultiPolygon(polys.map(pg => pg.getCoordinates())));
    merged.set(FIELD_COUNT, Math.max(Number(prev.get(FIELD_COUNT) || 0), Number(f.get(FIELD_COUNT) || 0)));
    byCode.set(code, merged);
  }
  return byCode;
}
// All currently loaded WFS rows of one code (for click-to-select on one piece of a split sector).
function loadedFeatureForCode(code, fallback) {
  if (!sectorSource) return fallback;
  const same = sectorSource.getFeatures().filter(f => readAdminCode(f) === code);
  if (same.length < 2) return fallback;
  return mergeFeaturesByCode(same).get(code) || fallback;
}

const STYLE_BASE = new Style({
  fill: new Fill({ color: 'rgba(43, 124, 233, 0.04)' }),
  stroke: new Stroke({ color: 'rgba(30, 99, 200, 0.5)', width: 1 })
});
const STYLE_SELECTED = new Style({
  fill: new Fill({ color: 'rgba(43, 124, 233, 0.30)' }),
  stroke: new Stroke({ color: '#1e63c8', width: 2 })
});
const STYLE_DROPPED = new Style({
  fill: new Fill({ color: 'rgba(120, 120, 120, 0.22)' }),
  stroke: new Stroke({ color: '#8a8a8a', width: 1.5, lineDash: [4, 4] })
});

// Green choropleth: shade selected sectors by target-group penetration (relative to the
// strongest sector in the current selection), so picking Dividuals visibly reshapes the map.
let maxPct = 0; // highest per-postcode penetration in the current selection (0 = no segmentation)
// NOTE: `Map` is the OpenLayers Map in this module (imported from 'ol'), so use a plain
// object for the style cache — `new Map()` here would build an OL map, not a JS Map.
const penCache = {};
// `far` = zoomed out beyond the WFS sector layer: sectors are a few pixels, so the pale end of
// the scale (0.14) vanished against the basemap and a selection looked unselected until you
// zoomed in and the sector layer drew its outlines. Zoomed out, the floor is 0.35 and the
// outline thicker; the relative shading is kept.
function penStyle(intensity, far = false) {
  const b = Math.max(0, Math.min(8, Math.round(intensity * 8)));
  const key = (far ? 'f' : 'n') + b;
  if (penCache[key]) return penCache[key];
  const a = far ? 0.35 + (b / 8) * 0.45 : 0.14 + (b / 8) * 0.56; // pale .. strong
  const st = new Style({
    fill: new Fill({ color: `rgba(31, 150, 90, ${a})` }),
    stroke: new Stroke({ color: '#1a7a4a', width: far ? 2 : 1.2 })
  });
  penCache[key] = st;
  return st;
}

function sectorStyle(feature, resolution) {
  const code = readAdminCode(feature);
  if (code && state.sectors.has(code)) {
    if (droppedCodes().has(code)) return STYLE_DROPPED;
    // With a segmentation active, colour by penetration; otherwise plain selected blue.
    if (state.dividuals.size > 0 && maxPct > 0) {
      const s = state.sectors.get(code);
      const p = s ? sectorPctOf(s) : null;
      if (typeof p === 'number') return penStyle(p / maxPct, resolution > SECTOR_MAX_RESOLUTION);
    }
    return STYLE_SELECTED;
  }
  return STYLE_BASE;
}

// Convert an OL sector feature into the demo's sector info object.
const gjIO = new GeoJSON();
function featureToInfo(feature) {
  const code = readAdminCode(feature);
  if (!code) return null;
  feature = loadedFeatureForCode(code, feature);
  const geom = feature.getGeometry();
  const [lng, lat] = toLonLat(getCenter(geom.getExtent()));
  return {
    nis9: code,
    totalHH: Number(feature.get(FIELD_COUNT) || 0),
    lng, lat,
    feature: gjIO.writeFeatureObject(feature, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' })
  };
}

// A point guaranteed inside the geometry (EPSG:3857), for draw-select hit testing.
function centroid3857(feature) {
  const g = feature.getGeometry();
  if (typeof g.getInteriorPoint === 'function') return g.getInteriorPoint().getCoordinates();
  return getCenter(g.getExtent());
}

export function initMap(targetId = 'map', opts = {}) {
  if (map) return map;
  const onToggle = typeof opts === 'function' ? opts : opts.onToggle;
  drawSelectCb = opts.onDrawSelect || null;

  sectorSource = new VectorSource({
    format: new GeoJSON(),
    strategy: bboxStrategy,
    url: (extent) =>
      `${GEOSERVER_URL}?service=WFS&version=1.0.0&request=GetFeature` +
      `&typename=${SECTOR_TYPENAME}&outputFormat=application/json` +
      `&srsname=EPSG:3857&maxFeatures=8000&bbox=${extent.join(',')},EPSG:3857` // lift the server's default cap
  });

  // The WFS layer only draws the faint base outlines (and serves click-to-select). The selection
  // itself is drawn by selectionLayer from our own clones: that layer is complete at every zoom,
  // whereas a WFS load for a wide viewport can be truncated, and a selected sector that was both
  // in the WFS layer and in the clone layer came out darker than one drawn once — the "lighter
  // blue" zones that looked unselected.
  sectorLayer = new VectorLayer({
    source: sectorSource,
    style: () => STYLE_BASE,
    maxResolution: SECTOR_MAX_RESOLUTION
  });

  selectionSource = new VectorSource();
  selectionLayer = new VectorLayer({ source: selectionSource, style: sectorStyle, zIndex: 50 });

  drawSource = new VectorSource();
  drawLayer = new VectorLayer({
    source: drawSource,
    zIndex: 60,
    style: new Style({
      fill: new Fill({ color: 'rgba(232, 145, 42, 0.12)' }),
      stroke: new Stroke({ color: '#e8912a', width: 2 })
    })
  });

  map = new Map({
    target: targetId,
    layers: [new TileLayer({ source: new OSM() }), sectorLayer, selectionLayer, drawLayer],
    view: new View({ center: fromLonLat([4.4025, 51.2194]), zoom: 13 }) // Antwerp — sectors visible
  });

  // Click-to-select (reuses pcSelection's toggle idea; here at sector granularity).
  clickHandler = (evt) => {
    if (drawInteraction || !selectEnabled) return; // only in Select mode; not while drawing or in Move mode
    let hit = null;
    map.forEachFeatureAtPixel(evt.pixel, (f, layer) => {
      if (layer === sectorLayer && !hit) hit = f;
    });
    if (!hit) return;
    const info = featureToInfo(hit);
    if (info && typeof onToggle === 'function') onToggle(info);
  };
  map.on('singleclick', clickHandler);

  // Debug hooks for headless verification only.
  window.__sectorCount = () => sectorSource.getFeatures().length;
  window.__mapCenter = () => (map ? toLonLat(map.getView().getCenter()).map(v => Math.round(v * 1000) / 1000) : null);
  window.__selCount = () => (selectionSource ? selectionSource.getFeatures().length : 0);
  window.__selCodes = () => (selectionSource ? selectionSource.getFeatures().map(readAdminCode) : []);
  window.__stateCodes = () => [...state.sectors.entries()].map(([k, v]) => k + (v.feature ? '' : ':nofeature'));
  window.__circleCount = () => (drawSource ? drawSource.getFeatures().length : 0);
  window.__clickCenter = () => {
    const size = map.getSize();
    if (!size) return false;
    const px = [size[0] / 2, size[1] / 2];
    if (clickHandler) { clickHandler({ pixel: px }); return true; }
    return false;
  };
  // Simulate a circle draw around a lon/lat (metres) — same code path as drawend.
  window.__drawCircleAt = (lng, lat, radiusM = 1500) => {
    const center = fromLonLat([lng, lat]);
    const poly = fromCircle(new CircleGeom(center, radiusM), 64);
    let n = 0; const infos = [];
    sectorSource.getFeatures().forEach((f) => {
      if (poly.intersectsCoordinate(centroid3857(f))) { const i = featureToInfo(f); if (i) { infos.push(i); n++; } }
    });
    if (typeof drawSelectCb === 'function') drawSelectCb(infos);
    return n;
  };

  return map;
}

export function getMap() { return map; }

// ---------- Draw-to-select (circle / polygon) ----------
// Reuses the upstream drawTools/pcSelection approach (OL Draw + fromCircle), but selection
// is purely client-side: every already-loaded WFS sector whose centroid falls inside the
// drawn shape is added (no turf, no spatial-search/polygon round-trip).
// Toggle click-to-select. Move mode calls this with false (pan only); Select mode with true.
export function setSelectEnabled(v) {
  selectEnabled = !!v;
  if (map) map.getTargetElement().style.cursor = v ? '' : 'grab';
}

export function stopDraw() {
  if (drawInteraction && map) { map.removeInteraction(drawInteraction); drawInteraction = null; }
  drawSource?.clear();
  if (map) map.getTargetElement().style.cursor = '';
}

function startDraw(type) {
  if (!map) return;
  stopDraw();
  drawInteraction = new Draw({ source: drawSource, type });
  map.addInteraction(drawInteraction);
  map.getTargetElement().style.cursor = 'crosshair';

  drawInteraction.on('drawend', (evt) => {
    let poly = evt.feature.getGeometry();
    if (type === 'Circle') poly = fromCircle(poly, 64);

    const infos = [];
    sectorSource.getFeatures().forEach((f) => {
      if (poly.intersectsCoordinate(centroid3857(f))) {
        const info = featureToInfo(f);
        if (info) infos.push(info);
      }
    });

    // One-shot: remove the interaction and clear the drawn shape.
    stopDraw();
    if (typeof drawSelectCb === 'function') drawSelectCb(infos);
  });
}

export function startDrawCircle() { startDraw('Circle'); }
export function startDrawPolygon() { startDraw('Polygon'); }

// Restyle after selection / threshold changes. `dropped` = Set of dropped NIS9 codes.
export function refreshStyles(dropped = new Set()) {
  window.__droppedSectors = dropped;
  // Recompute the penetration scale for the green choropleth.
  maxPct = 0;
  if (state.dividuals.size > 0) {
    state.sectors.forEach((s) => { const p = sectorPctOf(s); if (typeof p === 'number' && p > maxPct) maxPct = p; });
  }
  // Rebuild the highlight layer from selected sectors (so selection persists even when
  // the WFS sector isn't in the current viewport).
  if (!selectionSource) return;
  selectionSource.clear();
  const gj = new GeoJSON();
  state.sectors.forEach((s) => {
    if (!s.feature) return;
    const f = gj.readFeature(s.feature, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' });
    selectionSource.addFeature(f);
  });
  sectorLayer?.changed();
  selectionLayer?.changed();
}

export function flyTo(lng, lat, zoom = 13) {
  if (!map) return;
  map.getView().animate({ center: fromLonLat([lng, lat]), zoom, duration: 500 });
}

// Fetch sector features from the WFS within a bbox of ±halfKm (real km) around a centre,
// independent of the map's zoom/bbox-strategy. Returns sector infos (with geometry).
export async function fetchSectorsBBox(lng, lat, halfKm) {
  const [cx, cy] = fromLonLat([lng, lat]);
  // EPSG:3857 units are stretched by 1/cos(lat); scale so the bbox covers ~halfKm real.
  const d = (halfKm * 1000) / Math.cos((lat * Math.PI) / 180);
  const bbox = [cx - d, cy - d, cx + d, cy + d].join(',');
  const url = `${GEOSERVER_URL}?service=WFS&version=1.0.0&request=GetFeature` +
    `&typename=${SECTOR_TYPENAME}&outputFormat=application/json&srsname=EPSG:3857` +
    `&maxFeatures=8000&bbox=${bbox},EPSG:3857`; // lift the 2000 default cap so big bboxes aren't truncated
  const resp = await fetch(url);
  if (!resp.ok) return [];
  const data = await resp.json();
  const gj = new GeoJSON();
  const rows = (data.features || []).map((f) => gj.readFeature(f, { dataProjection: 'EPSG:3857', featureProjection: 'EPSG:3857' }));
  return [...mergeFeaturesByCode(rows).values()].map((feat) => {
    const code = readAdminCode(feat);
    if (!code) return null;
    const [flng, flat] = toLonLat(getCenter(feat.getGeometry().getExtent()));
    return {
      nis9: code,
      totalHH: Number(feat.get(FIELD_COUNT) || 0),
      lng: flng, lat: flat,
      feature: gj.writeFeatureObject(feat, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' })
    };
  }).filter(Boolean);
}

// Look up the municipality (gemeente) name at a point via the Councils WFS layer.
// The layer carries the readable name in Admin_NL (unlike the sector layer, where Admin_NL
// is the NIS9 code). Used to label selection groups with real names instead of NIS5 numbers.
export async function councilNameAt(lng, lat) {
  // Exact point-in-polygon. A bbox query is useless here: the WFS evaluates BBOX against the
  // municipalities' envelopes, so a 40 m box in Temse also "hits" Bornem and Antwerpen and
  // maxFeatures=1 returns whichever comes first (verified 2026-10-15). INTERSECTS with an EWKT
  // point (SRID prefix is required, a bare POINT matches nothing) passes the firewall.
  const cql = encodeURIComponent(`INTERSECTS(u108263geom,SRID=4326;POINT(${lng} ${lat}))`);
  const url = `${GEOSERVER_URL}?service=WFS&version=1.0.0&request=GetFeature` +
    `&typename=geolinq:u101411ViewGroupAdresType1&outputFormat=application/json&srsname=EPSG:3857` +
    `&maxFeatures=1&CQL_FILTER=${cql}`;
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    const data = await r.json();
    const p = (data.features || [])[0]?.properties || null;
    return p ? (p.u108267Admin_NL || p.u108266Admin_FR || p.u108265Admin_GER || null) : null;
  } catch { return null; }
}

// The NIS9 sector code of the sector CONTAINING a point (sector layer). Point-in-sector is exact,
// unlike "nearest sector centroid" which flips to a neighbour when the anchor is near a border —
// so this reliably resolves a municipality's NIS5 from an anchor point inside it.
// Fetch specific sectors by Statbel code (e.g. the "Wijk" rows of a Distritool export). The
// GeoServer sits behind a firewall that rejects CQL `IN (...)`, `OR` and POST filters, so the
// lookup goes per municipality: one `LIKE 'NIS5%'` request (~0.3 MB, ~0.3 s) returns all its
// sectors and we keep the requested codes. Returns Map<nis9, info> (codes without trailing '-').
export async function fetchSectorsByCodes(codes, onProgress) {
  const wanted = new Set(codes.map((c) => String(c).replace(/-+$/, '').trim()).filter(Boolean));
  const prefixes = [...new Set([...wanted].map((c) => c.slice(0, 5)))];
  const gj = new GeoJSON();
  // globalThis.Map: in this module `Map` is OpenLayers' map class (imported above).
  const out = new globalThis.Map();
  let done = 0;
  for (const prefix of prefixes) {
    const cql = encodeURIComponent(`${FIELD_ADMIN[0]} LIKE '${prefix}%'`);
    const url = `${GEOSERVER_URL}?service=WFS&version=1.0.0&request=GetFeature` +
      `&typename=${SECTOR_TYPENAME}&outputFormat=application/json&srsname=EPSG:3857` +
      `&maxFeatures=2000&CQL_FILTER=${cql}`;
    try {
      const resp = await fetch(url);
      if (resp.ok) {
        const data = await resp.json();
        const rows = (data.features || []).map((f) => gj.readFeature(f, { dataProjection: 'EPSG:3857', featureProjection: 'EPSG:3857' }));
        for (const [code, feat] of mergeFeaturesByCode(rows)) {
          if (!wanted.has(code) || out.has(code)) continue;
          const info = featureToInfo(feat);
          if (info) out.set(code, info);
        }
      } else {
        console.warn('sector lookup', prefix, 'HTTP', resp.status);
      }
    } catch (e) { console.warn('sector lookup failed', prefix, e); } // caller falls back to postcodes
    done++;
    if (onProgress) onProgress(done, prefixes.length);
  }
  return out;
}

// A point inside a stored sector feature (GeoJSON, EPSG:4326) as [lng, lat]; null without
// geometry. For a multipolygon, the interior point of its largest part.
export function interiorPointOf(geojsonFeature) {
  try {
    const g = gjIO.readGeometry(geojsonFeature.geometry);
    if (g.getType() === 'Polygon') return g.getInteriorPoint().getCoordinates().slice(0, 2);
    if (g.getType() === 'MultiPolygon') {
      let bestP = null, bestA = -1;
      for (const p of g.getPolygons()) { const a = p.getArea(); if (a > bestA) { bestA = a; bestP = p; } }
      return bestP ? bestP.getInteriorPoint().getCoordinates().slice(0, 2) : null;
    }
    return null;
  } catch { return null; }
}

// The boundary of a municipality (post-merger names, as GIS geo/search labels them) from the
// Councils layer, as an OL geometry in EPSG:4326. null when the name is unknown. Names are matched
// exactly (the layer is case-sensitive and the firewall blocks OR/IN, so no fuzzy fallback here).
export async function fetchCouncilGeometry(name) {
  const safe = String(name || '').replace(/'/g, "''").trim();
  if (!safe) return null;
  const cql = encodeURIComponent(`u108267Admin_NL='${safe}'`);
  const url = `${GEOSERVER_URL}?service=WFS&version=1.0.0&request=GetFeature` +
    `&typename=geolinq:u101411ViewGroupAdresType1&outputFormat=application/json&srsname=EPSG:4326` +
    `&maxFeatures=1&propertyName=u108263geom,u108267Admin_NL&CQL_FILTER=${cql}`;
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    const data = await r.json();
    const f = (data.features || [])[0];
    return f && f.geometry ? gjIO.readGeometry(f.geometry) : null;
  } catch { return null; }
}

// The sectors (infos from fetchSectorsInBBox) whose interior point lies inside a geometry.
export function sectorsInsideGeometry(infos, geom) {
  return infos.filter((s) => {
    const pt = (s.feature && interiorPointOf(s.feature)) || [s.lng, s.lat];
    return pt && pt[0] != null && geom.intersectsCoordinate(pt);
  });
}

export async function sectorNis9At(lng, lat) {
  // Exact point-in-polygon, same reason as councilNameAt: a bbox query matches envelopes, and
  // the big port sectors' envelopes cover Zwijndrecht, so "sector at the 2070 centroid" came back
  // as an Antwerpen (11002) sector and the whole municipality import went to the wrong NIS5.
  const cql = encodeURIComponent(`INTERSECTS(u108263geom,SRID=4326;POINT(${lng} ${lat}))`);
  const url = `${GEOSERVER_URL}?service=WFS&version=1.0.0&request=GetFeature` +
    `&typename=${SECTOR_TYPENAME}&outputFormat=application/json&srsname=EPSG:3857` +
    `&maxFeatures=1&CQL_FILTER=${cql}`;
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    const data = await r.json();
    const f = (data.features || [])[0];
    if (!f) return null;
    return readAdminCode(new GeoJSON().readFeature(f, { dataProjection: 'EPSG:3857', featureProjection: 'EPSG:3857' })) || null;
  } catch { return null; }
}

// Sectors whose centroid lies within radiusKm (straight-line) of a point.
export async function sectorsInRadius(lng, lat, radiusKm) {
  const infos = await fetchSectorsBBox(lng, lat, radiusKm);
  return infos.filter((s) => getDistance([lng, lat], [s.lng, s.lat]) <= radiusKm * 1000);
}

// Back-compat: small bbox around a pc4 centre (search-box "This area only" flow).
export async function sectorsAround(lng, lat, spanKm = 1.2) {
  return fetchSectorsBBox(lng, lat, spanKm);
}

// Draw a radius circle outline as a visual cue (persists until cleared).
export function showAreaCircle(lng, lat, radiusKm) {
  if (!drawSource) return;
  drawSource.clear();
  const rMeters = (radiusKm * 1000) / Math.cos((lat * Math.PI) / 180);
  const poly = fromCircle(new CircleGeom(fromLonLat([lng, lat]), rMeters), 96);
  drawSource.addFeature(new Feature(poly));
}
export function clearAreaCircle() { drawSource?.clear(); }

// Fit the view to the current selection highlight layer.
export function fitToSelection(padding = 60) {
  if (!map || !selectionSource || selectionSource.getFeatures().length === 0) return;
  const ext = selectionSource.getExtent();
  map.getView().fit(ext, { padding: [padding, padding, padding, padding], duration: 400, maxZoom: 14 });
}

// Fetch sectors across an explicit lon/lat bbox [minLng,minLat,maxLng,maxLat] — used to show a
// WHOLE province/municipality (not a radius blob). Capped; caller skips per-sector pc lookups.
export async function fetchSectorsInBBox(bbox, maxFeatures = 6000) {
  const [minLng, minLat, maxLng, maxLat] = bbox;
  const [x1, y1] = fromLonLat([minLng, minLat]);
  const [x2, y2] = fromLonLat([maxLng, maxLat]);
  const box = [Math.min(x1, x2), Math.min(y1, y2), Math.max(x1, x2), Math.max(y1, y2)].join(',');
  const url = `${GEOSERVER_URL}?service=WFS&version=1.0.0&request=GetFeature` +
    `&typename=${SECTOR_TYPENAME}&outputFormat=application/json&srsname=EPSG:3857` +
    `&maxFeatures=${maxFeatures}&bbox=${box},EPSG:3857`;
  const resp = await fetch(url);
  if (!resp.ok) return [];
  const data = await resp.json();
  const gj = new GeoJSON();
  const rows = (data.features || []).map((f) => gj.readFeature(f, { dataProjection: 'EPSG:3857', featureProjection: 'EPSG:3857' }));
  return [...mergeFeaturesByCode(rows).values()].map((feat) => {
    const code = readAdminCode(feat);
    if (!code) return null;
    const [flng, flat] = toLonLat(getCenter(feat.getGeometry().getExtent()));
    return { nis9: code, totalHH: Number(feat.get(FIELD_COUNT) || 0), lng: flng, lat: flat,
      feature: gj.writeFeatureObject(feat, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }) };
  }).filter(Boolean);
}

export function fitToBBox(bbox, padding = 40) {
  if (!map) return;
  const [minLng, minLat, maxLng, maxLat] = bbox;
  const ext = [...fromLonLat([minLng, minLat]), ...fromLonLat([maxLng, maxLat])];
  map.getView().fit(ext, { padding: [padding, padding, padding, padding], duration: 500, maxZoom: 12 });
}

// Zoom/pan to a set of GeoJSON (EPSG:4326) sector features — used by the "locate on map"
// buttons in the selection table (one sector, or all sectors of a municipality/province).
export function focusFeatures(geojsonFeatures, padding = 80) {
  if (!map || !geojsonFeatures || !geojsonFeatures.length) return;
  const gj = new GeoJSON();
  const tmp = new VectorSource();
  geojsonFeatures.forEach((gjf) => {
    if (gjf) tmp.addFeature(gj.readFeature(gjf, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }));
  });
  if (tmp.getFeatures().length === 0) return;
  map.getView().fit(tmp.getExtent(), { padding: [padding, padding, padding, padding], duration: 500, maxZoom: 15 });
}
